import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import wlLogoWhite from "../../img/wl-tagline-logo-white.png";
import propertyPhoto from "../../img/module-housing-rent.jpg";
import countries from "../../utils/countries.json";
import nationalities from "../../utils/nationalities.json";
import "./ViewingApplication.css";

// The backend expects country and nationality as their numeric ids. The form
// keeps working with names; these maps translate at the API boundary. Names
// are trimmed because the source data contains stray whitespace.
const COUNTRY_ID_BY_NAME = Object.fromEntries(
  countries.map((c) => [c.t_countries_country_name.trim(), c.t_countries_id])
);
const COUNTRY_NAME_BY_ID = Object.fromEntries(
  countries.map((c) => [c.t_countries_id, c.t_countries_country_name.trim()])
);
const NATIONALITY_ID_BY_NAME = Object.fromEntries(
  nationalities.map((n) => [
    n.t_client_nationality_name.trim(),
    n.t_client_nationality_id,
  ])
);
const NATIONALITY_NAME_BY_ID = Object.fromEntries(
  nationalities.map((n) => [
    n.t_client_nationality_id,
    n.t_client_nationality_name.trim(),
  ])
);

const countryId = (name) => COUNTRY_ID_BY_NAME[name.trim()] ?? null;
const nationalityId = (name) => NATIONALITY_ID_BY_NAME[name.trim()] ?? null;

// Ids come back from the backend; older applications may still hold names.
const countryName = (value) =>
  COUNTRY_NAME_BY_ID[value] ?? (typeof value === "string" ? value : "");
const nationalityName = (value) =>
  NATIONALITY_NAME_BY_ID[value] ?? (typeof value === "string" ? value : "");

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";
  
// Dummy data until the backend endpoint exists.
const DUMMY_APPLICATION = {
  ref: "2806LZ_77",
  imageUrl: "https://welocate.com/images/properties/2806LZ_77/De%20Kortestraat%2077,%20Gouda-%20(1).jpg",
  property: {
    name: "De Kortestraat 77",
    city: "Gouda",
    metaLine: "2806 LZ Gouda · House · 138 m² · 5 bedrooms",
    askingRent: "€2.500",
    freeFrom: "01/09/2026",
  },
};

// The landlord's requirements themselves are static; the form inputs are
// checked against them live.
const CRITERIA_RULES = {
  incomeMultiplier: 3,
  startWindow: { from: "2026-09-01", to: "2026-12-01" },
};

const PROFILE_OPTIONS = [
  {
    id: "employed",
    label: "Employed",
    description: "Contract, salary slips and bank statements",
    Icon: AttachFileIcon,
  },
  {
    id: "student",
    label: "Student",
    description: "Enrolment plus a guarantor who covers the rent",
    Icon: PeopleAltOutlinedIcon,
  },
  {
    id: "self-employed",
    label: "Self-employed",
    description: "KvK extract, tax declarations and bank statements",
    Icon: LeaderboardOutlinedIcon,
  },
];

// `months` is a number of months, or the string "permanent" (mirrors how the
// partner's contract length is sent to the backend).
const CONTRACT_OPTIONS = [
  { months: 3, note: "below minimum" },
  { months: 6, note: "below minimum" },
  { months: 9, note: "below minimum" },
  { months: 12 },
  { months: 15 },
  { months: 18 },
  { months: 21 },
  { months: 24 },
  { months: "permanent", label: "No end date" },
];

// The fixed start dates are the first of the next two months, worked out
// from today's date so the options stay current (on 15 Oct: 1 Nov and 1 Dec).
const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const firstOfUpcomingMonth = (monthsAhead) => {
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth() + monthsAhead, 1);
  const year = date.getFullYear();
  const month = date.getMonth();
  return {
    id: `${year}-${String(month + 1).padStart(2, "0")}-01`,
    label: `1 ${MONTH_ABBREVIATIONS[month]} ${year}`,
  };
};
const START_OPTIONS = [
  { id: "asap", label: "As soon as it is free" },
  firstOfUpcomingMonth(1),
  firstOfUpcomingMonth(2),
];

const EMPLOYMENT_LENGTH_OPTIONS = [
  { id: "3", label: "3 months" },
  { id: "6", label: "6 months" },
  { id: "9", label: "9 months" },
  { id: "12", label: "12 months" },
  { id: "18", label: "18 months" },
  { id: "24", label: "24 months" },
  { id: "permanent", label: "Permanent" },
];

const COUNTRY_OPTIONS = countries
  .map((c) => c.t_countries_country_name.trim())
  .sort((a, b) => a.localeCompare(b));

// Deduplicated because the source data repeats some names with
// different ids.
const NATIONALITY_OPTIONS = [
  ...new Set(nationalities.map((n) => n.t_client_nationality_name.trim())),
].sort((a, b) => a.localeCompare(b));

const INITIAL_EMPLOYMENT = {
  employer: "",
  contractLength: "permanent",
  income: "",
};

const INITIAL_HOUSEHOLD = {
  withPartner: "no",
  count: "",
  pets: "no",
  partnerFirstName: "",
  partnerLastName: "",
  partnerEmail: "",
  partnerMobile: "",
  partnerNationality: "",
  partnerEmployer: "",
  partnerContractLength: "",
  partnerIncome: "",
  petDetails: "",
  note: "",
};

const INITIAL_OFFER = {
  rent: "",
  contractMonths: 12,
  startChoice: "asap",
  customStart: "",
};

// The household's combined gross monthly income, derived from the employment
// and partner sections instead of asking for it again in the offer.
const combinedIncome = (profile, employment, household) => {
  const own =
    profile === "employed" ? Number(employment.income) || 0 : 0;
  const partner =
    household.withPartner === "yes"
      ? Number(household.partnerIncome) || 0
      : 0;
  return own + partner;
};

const INITIAL_DETAILS = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  nationality: "",
  street: "",
  houseNo: "",
  addition: "",
  postcode: "",
  city: "",
  country: "",
};

const SALARY_HINT = "From the past 3 months";
const BANK_HINT = "Original bank PDF, no screen prints";

// Each slot's id is the document slug the backend stores (numbered slots
// like `salary-slip-2` are separate slugs there), so uploads and waivers are
// sent and matched by id alone. The applicant's own documents are always
// asked, whatever the household size. The partner's set (same documents,
// `-partner` slugs) is only asked when the applicant rents with a partner or
// friend.
const EMPLOYED_SECTIONS = (withPartner) => [
  {
    title: "Identity & motivation",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      ...(withPartner
        ? [
            { id: "passport-id-partner", label: "Passport or ID (partner)" },
            { id: "residence-permit-partner", label: "Residence permit (partner)", optional: true },
          ]
        : []),
    ],
  },
  {
    title: "Employment & income",
    docs: [
      { id: "employment-contract", label: "Employment contract" },
      { id: "salary-slip-1", label: "Salary slip 1", hint: SALARY_HINT, pdfOnly: true },
      { id: "salary-slip-2", label: "Salary slip 2", hint: SALARY_HINT, pdfOnly: true },
      { id: "salary-slip-3", label: "Salary slip 3", hint: SALARY_HINT, pdfOnly: true },
      ...(withPartner
        ? [
            { id: "employment-contract-partner", label: "Employment contract (partner)" },
            { id: "salary-slip-partner-1", label: "Salary slip 1 (partner)", hint: SALARY_HINT, pdfOnly: true },
            { id: "salary-slip-partner-2", label: "Salary slip 2 (partner)", hint: SALARY_HINT, pdfOnly: true },
            { id: "salary-slip-partner-3", label: "Salary slip 3 (partner)", hint: SALARY_HINT, pdfOnly: true },
          ]
        : []),
    ],
  },
  {
    title: "Bank statements",
    docs: [
      { id: "bank-statement-1", label: "Bank statement 1", hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-2", label: "Bank statement 2", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-3", label: "Bank statement 3", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "proof-of-savings", label: "Proof of savings", optional: true, pdfOnly: true },
      ...(withPartner
        ? [
            { id: "bank-statement-partner-1", label: "Bank statement 1 (partner)", hint: BANK_HINT, pdfOnly: true },
            { id: "bank-statement-partner-2", label: "Bank statement 2 (partner)", optional: true, hint: BANK_HINT, pdfOnly: true },
            { id: "bank-statement-partner-3", label: "Bank statement 3 (partner)", optional: true, hint: BANK_HINT, pdfOnly: true },
            { id: "proof-of-savings-partner", label: "Proof of savings (partner)", optional: true, pdfOnly: true },
          ]
        : []),
    ],
  },
];

// Students have no income of their own, so the landlord looks at each
// tenant's guarantor (usually a parent) instead. The applicant is the 1st
// tenant; renting with a partner or friend adds a 2nd tenant with their own
// identity, enrolment and guarantor documents (the `-partner` and
// `-guarantor-2` slugs).
const STUDENT_SECTIONS = (withSecondTenant) => [
  {
    title: "Identity & enrolment",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      { id: "university-registration", label: "University registration" },
      ...(withSecondTenant
        ? [
            { id: "passport-id-partner", label: "Passport or ID (2nd tenant)" },
            { id: "residence-permit-partner", label: "Residence permit (2nd tenant)", optional: true },
            { id: "university-registration-partner", label: "University registration (2nd tenant)" },
          ]
        : []),
    ],
  },
  {
    title: "Guarantor 1st tenant",
    docs: [
      { id: "guarantor-letter", label: "Guarantor letter" },
      { id: "passport-id-guarantor-1", label: "Passport or ID (Guarantor 1st tenant)" },
      { id: "employment-contract-guarantor-1", label: "Employment contract (Guarantor 1st tenant)" },
      { id: "salary-slip-1-guarantor-1", label: "Salary slip 1 (Guarantor 1st tenant)", hint: SALARY_HINT, pdfOnly: true },
      { id: "salary-slip-2-guarantor-1", label: "Salary slip 2 (Guarantor 1st tenant)", hint: SALARY_HINT, pdfOnly: true },
      { id: "salary-slip-3-guarantor-1", label: "Salary slip 3 (Guarantor 1st tenant)", hint: SALARY_HINT, pdfOnly: true },
      { id: "bank-statement-1-guarantor-1", label: "Bank statement 1 (Guarantor 1st tenant)", hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-2-guarantor-1", label: "Bank statement 2 (Guarantor 1st tenant)", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-3-guarantor-1", label: "Bank statement 3 (Guarantor 1st tenant)", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "proof-of-savings-guarantor", label: "Proof of savings (Guarantor)", optional: true, pdfOnly: true },
    ],
  },
  ...(withSecondTenant
    ? [
        {
          title: "Guarantor 2nd tenant",
          docs: [
            { id: "guarantor-letter-partner", label: "Guarantor letter (2nd tenant)" },
            { id: "passport-id-guarantor-2", label: "Passport or ID (Guarantor 2nd tenant)" },
            { id: "employment-contract-guarantor-2", label: "Employment contract (Guarantor 2nd tenant)" },
            { id: "salary-slip-1-guarantor-2", label: "Salary slip 1 (Guarantor 2nd tenant)", hint: SALARY_HINT, pdfOnly: true },
            { id: "salary-slip-2-guarantor-2", label: "Salary slip 2 (Guarantor 2nd tenant)", hint: SALARY_HINT, pdfOnly: true },
            { id: "salary-slip-3-guarantor-2", label: "Salary slip 3 (Guarantor 2nd tenant)", hint: SALARY_HINT, pdfOnly: true },
            { id: "bank-statement-1-guarantor-2", label: "Bank statement 1 (Guarantor 2nd tenant)", hint: BANK_HINT, pdfOnly: true },
            { id: "bank-statement-2-guarantor-2", label: "Bank statement 2 (Guarantor 2nd tenant)", optional: true, hint: BANK_HINT, pdfOnly: true },
            { id: "bank-statement-3-guarantor-2", label: "Bank statement 3 (Guarantor 2nd tenant)", optional: true, hint: BANK_HINT, pdfOnly: true },
          ],
        },
      ]
    : []),
];

// Self-employed applicants prove their income with their business papers
// (KvK extract, two years of tax declarations and the company's bank
// statement) instead of an employment contract and salary slips. A partner
// only hands in identity documents (the `-partner` slugs).
const SELF_EMPLOYED_SECTIONS = (withPartner) => [
  {
    title: "Identity & motivation",
    docs: [
      { id: "motivation-letter", label: "Motivation letter" },
      { id: "passport-id", label: "Passport or ID" },
      { id: "residence-permit", label: "Residence permit", optional: true },
      ...(withPartner
        ? [
            { id: "passport-id-partner", label: "Passport or ID (partner)" },
            { id: "residence-permit-partner", label: "Residence permit (partner)", optional: true },
          ]
        : []),
    ],
  },
  {
    title: "Business & income",
    docs: [
      { id: "kvk-extract", label: "KvK extract" },
      { id: "tax-declaration-1", label: "Tax declaration (year 1)", pdfOnly: true },
      { id: "tax-declaration-2", label: "Tax declaration (year 2)", pdfOnly: true },
      { id: "company-bank-statement", label: "Company bank statement", hint: BANK_HINT, pdfOnly: true },
    ],
  },
  {
    title: "Bank statements",
    docs: [
      { id: "bank-statement-1", label: "Bank statement 1", hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-2", label: "Bank statement 2", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "bank-statement-3", label: "Bank statement 3", optional: true, hint: BANK_HINT, pdfOnly: true },
      { id: "proof-of-savings", label: "Proof of savings", optional: true, pdfOnly: true },
    ],
  },
  {
    title: "Other",
    docs: [
      { id: "other-documents", label: "Other documents", optional: true },
    ],
  },
];

const buildDocumentSections = (profile, withPartner) => {
  if (profile === "student") return STUDENT_SECTIONS(withPartner);
  if (profile === "self-employed") return SELF_EMPLOYED_SECTIONS(withPartner);
  return EMPLOYED_SECTIONS(withPartner);
};

const flattenDocs = (sections) => sections.flatMap((section) => section.docs);

const DEFAULT_ALL_DOCS = flattenDocs(buildDocumentSections("employed", false));

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatEuro = (value) => `€${Math.round(value).toLocaleString("nl-NL")}`;

// "2026-09-08" -> "08/09/2026".
const formatDate = (isoDate) => {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value) => EMAIL_REGEX.test(value.trim());

// An optional leading +, common separators, and 8 to 15 digits.
const isValidMobile = (value) => {
  const digits = value.replace(/\D/g, "");
  return (
    /^\+?[0-9\s()./-]+$/.test(value.trim()) &&
    digits.length >= 8 &&
    digits.length <= 15
  );
};

const MAX_RENT_OFFER = 10000;
const MAX_MONTHLY_INCOME = 100000;
const MAX_HOUSE_NUMBER = 99999;
const MAX_STREET_LENGTH = 100;
const MAX_POSTCODE_LENGTH = 7;
const MAX_EMPLOYER_LENGTH = 30;

// Whole euros only, clamped to the field's maximum.
const toClampedInt = (value, max) => {
  if (value === "") return "";
  const amount = Math.floor(Math.abs(Number(value)));
  return String(Math.min(amount, max));
};

const blockNonIntegerKeys = (event) => {
  if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
    event.preventDefault();
  }
};

const MAX_HOUSEHOLD_COUNT = 10;
const isValidHouseholdCount = (value) => {
  const count = Number(value);
  return Number.isInteger(count) && count >= 1 && count <= MAX_HOUSEHOLD_COUNT;
};

const buildApplicationPayload = ({
  profile,
  details,
  employment,
  household,
  offer,
}) => ({
  profile,
  details: {
    first_name: details.firstName,
    last_name: details.lastName,
    email: details.email,
    mobile: details.mobile,
    nationality: nationalityId(details.nationality),
    street: details.street,
    house_number: Number(details.houseNo) || null,
    addition: details.addition || null,
    postcode: details.postcode,
    city: details.city,
    country: countryId(details.country),
  },
  employment:
    profile === "employed"
      ? {
          employer: employment.employer,
          contract_length: employment.contractLength,
          monthly_income: Number(employment.income) || null,
        }
      : null,
  household: {
    with_partner: household.withPartner === "yes",
    count: Number(household.count) || null,
    pets: household.pets === "yes",
    partner_first_name:
      household.withPartner === "yes"
        ? household.partnerFirstName || null
        : null,
    partner_last_name:
      household.withPartner === "yes"
        ? household.partnerLastName || null
        : null,
    partner_email:
      household.withPartner === "yes" ? household.partnerEmail || null : null,
    partner_mobile:
      household.withPartner === "yes" ? household.partnerMobile || null : null,
    partner_nationality:
      household.withPartner === "yes"
        ? nationalityId(household.partnerNationality)
        : null,
    // Only employed applicants are asked about their partner's employment;
    // a student's or self-employed applicant's income comes from the
    // documents.
    partner_employer:
      household.withPartner === "yes" && profile === "employed"
        ? household.partnerEmployer || null
        : null,
    // Months as a number, or the string "permanent".
    partner_contract_length:
      household.withPartner === "yes" && profile === "employed"
        ? household.partnerContractLength === "permanent"
          ? "permanent"
          : Number(household.partnerContractLength) || null
        : null,
    partner_monthly_gross_income:
      household.withPartner === "yes" && profile === "employed"
        ? Number(household.partnerIncome) || null
        : null,
    pet_details: household.petDetails || null,
    note: household.note || null,
  },
  offer: {
    monthly_rent: Number(offer.rent) || null,
    monthly_income: combinedIncome(profile, employment, household) || null,
    // "No end date" is sent as 999; the backend expects an integer.
    contract_months:
      offer.contractMonths === "permanent" ? 999 : offer.contractMonths,
    start_choice: offer.startChoice,
    custom_start:
      offer.startChoice === "custom" ? offer.customStart || null : null,
  },
});

// Returns only the parts of `next` that differ from `prev`. Sections
// (details, offer, ...) are compared field by field; a section is included
// only when at least one of its fields changed.
const diffPayload = (prev, next) => {
  const patch = {};
  Object.keys(next).forEach((key) => {
    const before = prev[key];
    const after = next[key];
    if (
      before &&
      after &&
      typeof before === "object" &&
      typeof after === "object"
    ) {
      const changed = {};
      Object.keys(after).forEach((field) => {
        if (before[field] !== after[field]) changed[field] = after[field];
      });
      if (Object.keys(changed).length > 0) patch[key] = changed;
    } else if (before !== after) {
      patch[key] = after;
    }
  });
  return patch;
};

const ConflictWarning = ({ children }) => (
  <div className="va-conflict">
    <PriorityHighIcon className="va-conflict-icon" />
    <span>{children}</span>
  </div>
);

const Field = ({ label, optional, span, helper, error, children }) => (
  <div className={`va-field${span ? ` va-field--${span}` : ""}`}>
    <label className="va-label">
      {label}
      {optional ? (
        <span className="va-optional">Optional</span>
      ) : (
        <span className="va-required">*</span>
      )}
    </label>
    {children}
    {error ? (
      <p className="va-field-error">{error}</p>
    ) : (
      helper && <p className="va-helper">{helper}</p>
    )}
  </div>
);

const ViewingApplication = () => {
  const { whRefId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId");
  // Only hydrate from the backend when the page loads with an applicationId
  // already in the url (refresh / shared link), not right after our own POST.
  const [initialApplicationId] = useState(applicationId);
  // An existing Welocate client can be linked in the url; their profile
  // prefills the form for a new application. Captured once so a later url
  // change (our own POST adding applicationId) doesn't refetch.
  const [initialClientId] = useState(searchParams.get("clientId"));

  const [application] = useState(DUMMY_APPLICATION);
  const [listing, setListing] = useState(null);
  // "loading" | "found" | "not-found"; the form only renders once the listing
  // in the url is confirmed to exist.
  const [listingStatus, setListingStatus] = useState("loading");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState("employed");
  const [details, setDetails] = useState(INITIAL_DETAILS);
  const [offer, setOffer] = useState(INITIAL_OFFER);
  const [employment, setEmployment] = useState(INITIAL_EMPLOYMENT);
  const [household, setHousehold] = useState(INITIAL_HOUSEHOLD);
  const [uploads, setUploads] = useState({});
  // Required documents the user marked as not applicable, keyed by doc id.
  const [waivers, setWaivers] = useState({});
  const [selectedDocId, setSelectedDocId] = useState(DEFAULT_ALL_DOCS[0].id);
  // The "doesn't apply to me" reason form for the selected document.
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverReason, setWaiverReason] = useState("");
  const [waiverError, setWaiverError] = useState(null);
  const [waiverSaving, setWaiverSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // The one document group unfolded in the sidebar; it follows the selected
  // document and can be switched by hand.
  const [openSectionTitle, setOpenSectionTitle] = useState(null);
  const fileInputRef = useRef(null);
  // Payload as the backend last saw it, used to send only changed fields.
  const lastSavedRef = useRef(null);

  // The catalog follows the profile (students hand in guarantor documents,
  // self-employed applicants their business papers, instead of their own
  // employment income); the partner's documents are only asked when
  // renting with a partner or friend.
  const withPartner = household.withPartner === "yes";
  const documentSections = useMemo(
    () => buildDocumentSections(profile, withPartner),
    [profile, withPartner]
  );
  const allDocs = useMemo(
    () => flattenDocs(documentSections),
    [documentSections]
  );
  const requiredDocs = useMemo(
    () => allDocs.filter((doc) => !doc.optional),
    [allDocs]
  );

  // Switching profile or answering "no" to the partner question can remove
  // the selected slot.
  useEffect(() => {
    if (!allDocs.some((doc) => doc.id === selectedDocId)) {
      setSelectedDocId(allDocs[0].id);
    }
  }, [allDocs, selectedDocId]);

  const fetchApplication = useCallback(async (id) => {
    const response = await fetch(`${BACKEND_URL}/welocate/applications/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to load application (${response.status})`);
    }
    return response.json();
  }, []);

  // Hydrates the form state from a backend application response.
  const applyApplicationData = useCallback((data) => {
    const d = data.details || {};
    const h = data.household || {};
    const o = data.offer || {};
    const newProfile = data.profile || "employed";
    const newDetails = {
      firstName: d.first_name ?? "",
      lastName: d.last_name ?? "",
      email: d.email ?? "",
      mobile: d.mobile ?? "",
      nationality: d.nationality != null ? nationalityName(d.nationality) : "",
      street: d.street ?? "",
      houseNo: d.house_number != null ? String(d.house_number) : "",
      addition: d.addition ?? "",
      postcode: d.postcode ?? "",
      city: d.city ?? "",
      country: d.country != null ? countryName(d.country) : "",
    };
    const newEmployment = data.employment
      ? {
          employer: data.employment.employer ?? "",
          contractLength: data.employment.contract_length ?? "permanent",
          income:
            data.employment.monthly_income != null
              ? String(data.employment.monthly_income)
              : "",
        }
      : null;
    const newHousehold = {
      withPartner: h.with_partner ? "yes" : "no",
      count: h.count != null ? String(h.count) : "",
      pets: h.pets ? "yes" : "no",
      partnerFirstName: h.partner_first_name ?? "",
      partnerLastName: h.partner_last_name ?? "",
      partnerEmail: h.partner_email ?? "",
      partnerMobile: h.partner_mobile ?? "",
      partnerNationality:
        h.partner_nationality != null
          ? nationalityName(h.partner_nationality)
          : "",
      partnerEmployer: h.partner_employer ?? "",
      partnerContractLength:
        h.partner_contract_length != null
          ? String(h.partner_contract_length)
          : "",
      partnerIncome:
        h.partner_monthly_gross_income != null
          ? String(h.partner_monthly_gross_income)
          : "",
      petDetails: h.pet_details ?? "",
      note: h.note ?? "",
    };
    const staleStart =
      o.start_choice &&
      o.start_choice !== "asap" &&
      o.start_choice !== "custom" &&
      !START_OPTIONS.some((option) => option.id === o.start_choice);
    const newOffer = {
      rent: o.monthly_rent != null ? String(o.monthly_rent) : "",
      contractMonths:
        o.contract_months === 999 ? "permanent" : o.contract_months ?? 12,
      // A saved fixed date that has since dropped out of the rolling
      // options is restored as a custom date so it still shows as selected.
      startChoice: staleStart ? "custom" : o.start_choice ?? "asap",
      customStart: staleStart ? o.start_choice : o.custom_start ?? "",
    };
    setProfile(newProfile);
    setDetails(newDetails);
    if (newEmployment) setEmployment(newEmployment);
    setHousehold(newHousehold);
    setOffer(newOffer);
    // Match server documents against the doc list for the fetched profile
    // and partner situation, so every partner and guarantor slot hydrates.
    const docsForHousehold = flattenDocs(
      buildDocumentSections(newProfile, newHousehold.withPartner === "yes")
    );
    const serverUploads = {};
    (data.document_uploads || []).forEach((entry) => {
      const doc = docsForHousehold.find((d) => d.id === entry.document_slug);
      if (!doc) return;
      serverUploads[doc.id] =
        entry.status === "rejected"
          ? {
              uploadId: entry.id,
              name: entry.filename,
              status: "rejected",
              headline: "This document needs a new file.",
              detail: "Upload a new version to replace it.",
            }
          : {
              uploadId: entry.id,
              name: entry.filename,
              status: "accepted",
              headline: "This document is fine. Nothing to do.",
              detail: "We stored it with your application.",
            };
    });
    setUploads((prev) => {
      const next = { ...serverUploads };
      // Entries uploaded in this session carry the File itself (size,
      // in-flight status); keep those over the server's version, but take
      // the backend's upload id so Remove can delete them server-side.
      Object.keys(prev).forEach((docId) => {
        if (prev[docId]?.file) {
          next[docId] = {
            ...prev[docId],
            uploadId: serverUploads[docId]?.uploadId ?? prev[docId].uploadId,
          };
        }
      });
      return next;
    });
    const serverWaivers = {};
    (data.document_waivers || []).forEach((entry) => {
      const doc = docsForHousehold.find((d) => d.id === entry.document_slug);
      if (!doc) return;
      serverWaivers[doc.id] = { waiverId: entry.id, reason: entry.reason };
    });
    setWaivers(serverWaivers);
    setIsSubmitted(Boolean(data.is_submitted));
    if (data.is_submitted) setConfirmed(true);
    lastSavedRef.current = buildApplicationPayload({
      profile: newProfile,
      details: newDetails,
      employment: newEmployment || INITIAL_EMPLOYMENT,
      household: newHousehold,
      offer: newOffer,
    });
  }, []);

  const fetchClient = useCallback(async (id) => {
    const response = await fetch(`${BACKEND_URL}/welocate/clients/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to load client (${response.status})`);
    }
    return response.json();
  }, []);

  // Prefills the form from a Welocate client record. The client api sends
  // unset numbers as 0 and unset strings as null or "", so only values that
  // are actually filled in overwrite the form's defaults. The profile and
  // the contract length of an employment are not stored on the client, so
  // those keep their defaults.
  const applyClientData = useCallback((client) => {
    const text = (value) => (typeof value === "string" ? value : "");
    const positive = (value) => (Number(value) > 0 ? Number(value) : null);
    const numberText = (value) =>
      positive(value) != null ? String(positive(value)) : "";
    const lookupName = (toName, value) =>
      positive(value) != null ? toName(positive(value)) : "";

    setDetails((prev) => ({
      ...prev,
      firstName: text(client.first_name) || prev.firstName,
      lastName: text(client.last_name) || prev.lastName,
      email: text(client.email) || prev.email,
      mobile: text(client.mobile) || prev.mobile,
      nationality:
        lookupName(nationalityName, client.nationality) || prev.nationality,
      street: text(client.street) || prev.street,
      houseNo: numberText(client.house_number) || prev.houseNo,
      addition: text(client.floor) || prev.addition,
      postcode: text(client.postcode) || prev.postcode,
      city: text(client.city) || prev.city,
      country: lookupName(countryName, client.country) || prev.country,
    }));

    setEmployment((prev) => ({
      ...prev,
      employer: text(client.employer) || prev.employer,
      income: numberText(client.income) || prev.income,
    }));

    const rentingJointly = client.renting_jointly === true;
    setHousehold((prev) => ({
      ...prev,
      withPartner: rentingJointly ? "yes" : prev.withPartner,
      count: numberText(client.number_in_household) || prev.count,
      pets: client.has_pets === true ? "yes" : prev.pets,
      partnerFirstName: text(client.partner_first_name) || prev.partnerFirstName,
      partnerLastName: text(client.partner_last_name) || prev.partnerLastName,
      partnerEmail: text(client.partner_email) || prev.partnerEmail,
      partnerMobile: text(client.partner_mobile) || prev.partnerMobile,
      partnerNationality:
        lookupName(nationalityName, client.partner_nationality) ||
        prev.partnerNationality,
      partnerEmployer: text(client.partner_employer) || prev.partnerEmployer,
      partnerIncome: numberText(client.partner_income) || prev.partnerIncome,
    }));

    // Contract length is in months (999 meaning no end date, like the
    // application payload); only lengths the form offers are taken over.
    const months = positive(client.contract_length);
    const contractMonths =
      months === 999
        ? "permanent"
        : CONTRACT_OPTIONS.some((option) => option.months === months)
        ? months
        : null;
    // The start date arrives as an iso date(time); a date that matches one
    // of the fixed choices selects that pill, any other date is custom.
    const startDate = text(client.contract_start_date).slice(0, 10);
    const startChoice = !startDate
      ? null
      : START_OPTIONS.some((option) => option.id === startDate)
      ? startDate
      : "custom";
    setOffer((prev) => ({
      ...prev,
      rent: numberText(client.monthly_rental_offer) || prev.rent,
      contractMonths: contractMonths ?? prev.contractMonths,
      startChoice: startChoice ?? prev.startChoice,
      customStart: startChoice === "custom" ? startDate : prev.customStart,
    }));
  }, []);

  useEffect(() => {
    if (!whRefId) {
      setListingStatus("not-found");
      return undefined;
    }
    let cancelled = false;
    const loadListing = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/welocate/listings/${whRefId}`
        );
        if (!response.ok) {
          throw new Error(`Failed to load listing (${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setListing(data);
          setListingStatus("found");
        }
      } catch (error) {
        console.error("Could not load listing", error);
        if (!cancelled) setListingStatus("not-found");
      }
    };
    loadListing();
    return () => {
      cancelled = true;
    };
  }, [whRefId]);

  useEffect(() => {
    if (!initialApplicationId) return undefined;
    let cancelled = false;
    const loadApplication = async () => {
      try {
        const data = await fetchApplication(initialApplicationId);
        if (!cancelled) applyApplicationData(data);
      } catch (error) {
        console.error("Could not load application", error);
      }
    };
    loadApplication();
    return () => {
      cancelled = true;
    };
  }, [initialApplicationId, fetchApplication, applyApplicationData]);

  // An existing application already holds the applicant's data, so the
  // client profile only prefills a fresh form.
  useEffect(() => {
    if (!initialClientId || initialApplicationId) return undefined;
    let cancelled = false;
    const loadClient = async () => {
      try {
        const data = await fetchClient(initialClientId);
        if (!cancelled) applyClientData(data);
      } catch (error) {
        console.error("Could not load client", error);
      }
    };
    loadClient();
    return () => {
      cancelled = true;
    };
  }, [initialClientId, initialApplicationId, fetchClient, applyClientData]);

  // Only applies the patch if the upload was not replaced or removed while
  // the verification request was in flight.
  const patchUpload = (docId, file, patch) =>
    setUploads((prev) => {
      const current = prev[docId];
      if (!current || current.file !== file) return prev;
      return { ...prev, [docId]: { ...current, ...patch } };
    });

  const uploadDocument = async (docId, file) => {
    if (!applicationId) {
      patchUpload(docId, file, {
        status: "rejected",
        headline: "We could not store this document.",
        detail: "Save your details in step 1 first, then upload it again.",
      });
      return;
    }
    const body = new FormData();
    body.append("file", file);
    body.append("document_slug", docId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads`,
        { method: "POST", body }
      );
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      patchUpload(docId, file, {
        status: "accepted",
        headline: "This document is fine. Nothing to do.",
        detail: "We stored it with your application.",
      });
      try {
        applyApplicationData(await fetchApplication(applicationId));
      } catch (error) {
        console.error("Could not refresh application", error);
      }
    } catch (error) {
      patchUpload(docId, file, {
        status: "rejected",
        headline: "We could not store this document.",
        detail:
          "Something went wrong while uploading your file. Remove it and try again.",
      });
    }
  };

  const handleFile = (docId, file) => {
    if (!file || isSubmitted) return;
    const entry = { file, name: file.name, size: file.size };
    const doc = allDocs.find((d) => d.id === docId);
    const isAllowedType = doc?.pdfOnly
      ? file.type === "application/pdf"
      : file.type === "application/pdf" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.type.startsWith("image/");
    if (!isAllowedType) {
      setUploads((prev) => ({
        ...prev,
        [docId]: {
          ...entry,
          status: "rejected",
          headline: doc?.pdfOnly
            ? "We can only accept this document as a PDF."
            : "We can only read PDF, Word and photo files.",
          detail: doc?.pdfOnly
            ? "Download the original PDF from your bank or payroll portal and upload that."
            : "Upload the document as a PDF, Word file (DOCX) or a photo (JPG or PNG).",
        },
      }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setUploads((prev) => ({
        ...prev,
        [docId]: {
          ...entry,
          status: "rejected",
          headline: "This file is larger than 10 MB.",
          detail: "Compress it or take a new photo, then try again.",
        },
      }));
      return;
    }
    setUploads((prev) => ({
      ...prev,
      [docId]: { ...entry, status: "processing" },
    }));
    uploadDocument(docId, file);
  };

  const removeUpload = async (docId) => {
    const upload = uploads[docId];
    // Clear it from the form right away; entries that never reached the
    // backend (validation failures, missing application) have no uploadId.
    setUploads((prev) => {
      const { [docId]: removed, ...rest } = prev;
      return rest;
    });
    if (!applicationId || !upload?.uploadId) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/${upload.uploadId}`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error(`Delete failed (${response.status})`);
      }
    } catch (error) {
      console.error("Could not delete document", error);
    }
  };

  // Unfold the group holding the selected document, whichever way it was
  // selected (sidebar, "next document" button, profile change).
  useEffect(() => {
    const section = documentSections.find((s) =>
      s.docs.some((doc) => doc.id === selectedDocId)
    );
    if (section) setOpenSectionTitle(section.title);
  }, [documentSections, selectedDocId]);

  // Switching documents always closes the "doesn't apply" reason form.
  const selectDoc = (docId) => {
    setSelectedDocId(docId);
    setWaiverOpen(false);
    setWaiverReason("");
    setWaiverError(null);
  };

  const submitWaiver = async () => {
    const reason = waiverReason.trim();
    if (!reason) {
      setWaiverError("Please tell us why this document does not apply to you.");
      return;
    }
    if (!applicationId) {
      setWaiverError("Save your details in step 1 first, then try again.");
      return;
    }
    setWaiverSaving(true);
    setWaiverError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/applications/${applicationId}/document-waivers`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ document_slug: selectedDocId, reason }),
        }
      );
      if (!response.ok) {
        throw new Error(`Saving failed (${response.status})`);
      }
      // The backend returns the new waiver id, either directly as a string
      // or as an object with an id.
      let waiverId = null;
      try {
        const data = await response.json();
        waiverId = typeof data === "string" ? data : data?.id ?? null;
      } catch (error) {
        console.warn("Could not read the waiver id", error);
      }
      setWaivers((prev) => ({
        ...prev,
        [selectedDocId]: { waiverId, reason },
      }));
      setWaiverOpen(false);
      setWaiverReason("");
    } catch (error) {
      console.error("Could not save waiver", error);
      setWaiverError(
        "We could not save this. Check your connection and try again."
      );
    } finally {
      setWaiverSaving(false);
    }
  };

  const removeWaiver = async (docId) => {
    const waiver = waivers[docId];
    setWaivers((prev) => {
      const { [docId]: removed, ...rest } = prev;
      return rest;
    });
    if (!applicationId || !waiver?.waiverId) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/applications/${applicationId}/document-waivers/${waiver.waiverId}`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error(`Delete failed (${response.status})`);
      }
    } catch (error) {
      console.error("Could not delete waiver", error);
    }
  };

  const setField = (field) => (event) =>
    setDetails((prev) => ({ ...prev, [field]: event.target.value }));

  const setEmploymentField = (field) => (event) =>
    setEmployment((prev) => ({ ...prev, [field]: event.target.value }));

  const setHouseholdField = (field) => (event) =>
    setHousehold((prev) => ({ ...prev, [field]: event.target.value }));

  const setOfferField = (field) => (event) =>
    setOffer((prev) => ({ ...prev, [field]: event.target.value }));

  // Live validation errors, only shown once the field has content.
  const emailError =
    details.email && !isValidEmail(details.email)
      ? "This doesn't look like a valid email address."
      : null;
  const mobileError =
    details.mobile && !isValidMobile(details.mobile)
      ? "This doesn't look like a valid mobile number."
      : null;
  const countError =
    household.count && !isValidHouseholdCount(household.count)
      ? `Enter a number between 1 and ${MAX_HOUSEHOLD_COUNT}.`
      : null;
  const partnerEmailError =
    household.partnerEmail && !isValidEmail(household.partnerEmail)
      ? "This doesn't look like a valid email address."
      : null;
  const partnerMobileError =
    household.partnerMobile && !isValidMobile(household.partnerMobile)
      ? "This doesn't look like a valid mobile number."
      : null;

  // Everything still stopping the form from being complete, in the order
  // the fields appear on the page. Each check covers one input and yields
  // the message for its first failing rule, so a field counts once; `target`
  // is the id of the input the hint jumps to when clicked. The first open
  // item doubles as the guidance shown next to the disabled continue button
  // and the total feeds the "to fix" counter in the step header.
  // Students rent with a "2nd tenant"; employed applicants with a partner.
  const coTenant = profile === "student" ? "the 2nd tenant" : "your partner";
  const openItems = (() => {
    const checks = [
      ["field-first-name", () =>
        !details.firstName.trim() && "Fill in your first name."],
      ["field-last-name", () =>
        !details.lastName.trim() && "Fill in your last name."],
      ["field-nationality", () =>
        !details.nationality.trim() && "Fill in your nationality."],
      ["field-email", () =>
        (!details.email.trim() && "Fill in your email address.") ||
        (!isValidEmail(details.email) && "Enter a valid email address.")],
      ["field-mobile", () =>
        (!details.mobile.trim() && "Fill in your mobile number.") ||
        (!isValidMobile(details.mobile) && "Enter a valid mobile number.")],
      ["field-street", () => !details.street.trim() && "Fill in your street."],
      ["field-house-no", () =>
        !(Number(details.houseNo) > 0) && "Fill in your house number."],
      ["field-postcode", () =>
        !details.postcode.trim() && "Fill in your postcode."],
      ["field-city", () => !details.city.trim() && "Fill in your city."],
      ["field-country", () =>
        !details.country.trim() && "Fill in your country."],
    ];
    if (profile === "employed") {
      checks.push(
        ["field-employer", () =>
          !employment.employer.trim() && "Fill in your employer."],
        ["field-income", () =>
          !(Number(employment.income) > 0) &&
          "Fill in your monthly gross income."]
      );
    }
    checks.push(["field-household-count", () =>
      (!household.count.trim() && "Fill in the number in your household.") ||
      (!isValidHouseholdCount(household.count) &&
        `The number in your household must be between 1 and ${MAX_HOUSEHOLD_COUNT}.`)]);
    if (household.withPartner === "yes") {
      checks.push(
        ["field-partner-first-name", () =>
          !household.partnerFirstName.trim() &&
          `Fill in ${coTenant}'s first name.`],
        ["field-partner-last-name", () =>
          !household.partnerLastName.trim() &&
          `Fill in ${coTenant}'s last name.`],
        ["field-partner-email", () =>
          (!household.partnerEmail.trim() &&
            `Fill in ${coTenant}'s email address.`) ||
          (!isValidEmail(household.partnerEmail) &&
            `Enter a valid email address for ${coTenant}.`)],
        ["field-partner-mobile", () =>
          (!household.partnerMobile.trim() &&
            `Fill in ${coTenant}'s mobile number.`) ||
          (!isValidMobile(household.partnerMobile) &&
            `Enter a valid mobile number for ${coTenant}.`)],
        ["field-partner-nationality", () =>
          !household.partnerNationality.trim() &&
          `Fill in ${coTenant}'s nationality.`]
      );
      // The partner's employer and contract length are optional.
      if (profile === "employed") {
        checks.push(["field-partner-income", () =>
          !(Number(household.partnerIncome) > 0) &&
          "Fill in your partner's monthly gross income."]);
      }
    }
    if (household.pets === "yes") {
      checks.push(["field-pet-details", () =>
        !household.petDetails.trim() && "Tell us which pet you have."]);
    }
    checks.push(["field-offer-rent", () =>
      !(Number(offer.rent) > 0) && "Fill in your monthly rental offer."]);
    if (offer.startChoice === "custom") {
      checks.push(["field-custom-start", () =>
        !offer.customStart && "Pick your desired start date."]);
    }
    checks.push(["field-confirm", () =>
      !confirmed && "Tick the confirmation box to continue."]);

    return checks
      .map(([target, check]) => ({ target, message: check() }))
      .filter((item) => item.message);
  })();
  const incompleteHint = openItems[0] ?? null;
  const detailsToFix = openItems.length;
  const formComplete = !incompleteHint;

  const focusField = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.focus({ preventScroll: true });
  };

  const submitDetails = async () => {
    // A submitted application is read-only; just move between the steps.
    if (isSubmitted) {
      setStep(2);
      return;
    }
    if (!formComplete) return;
    const payload = buildApplicationPayload({
      profile,
      details,
      employment,
      household,
      offer,
    });

    // Existing application: PATCH only the fields that changed since the
    // last save, or skip the call entirely when nothing changed.
    if (applicationId) {
      const patch = lastSavedRef.current
        ? diffPayload(lastSavedRef.current, payload)
        : payload;
      if (Object.keys(patch).length === 0) {
        setStep(2);
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const response = await fetch(
          `${BACKEND_URL}/welocate/applications/${applicationId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
          }
        );
        if (!response.ok) {
          throw new Error(`Saving failed (${response.status})`);
        }
        lastSavedRef.current = payload;
        setStep(2);
      } catch (error) {
        console.error("Could not update application", error);
        setSubmitError(
          "We could not save your changes. Check your connection and try again."
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/listings/${whRefId}/applications`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // A linked Welocate client is sent along so the backend can tie
          // the new application to that client.
          body: JSON.stringify(
            initialClientId ? { ...payload, client_id: initialClientId } : payload
          ),
        }
      );
      if (!response.ok) {
        throw new Error(`Saving failed (${response.status})`);
      }
      // The backend returns the new application id directly as a string.
      const newId = await response.json();
      if (typeof newId === "string" && newId) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("applicationId", newId);
            return next;
          },
          { replace: true }
        );
      } else {
        console.warn("Expected the application id as a string, got:", newId);
      }
      lastSavedRef.current = payload;
      setStep(2);
    } catch (error) {
      console.error("Could not save application", error);
      setSubmitError(
        "We could not save your details. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendApplication = async () => {
    if (!applicationId) {
      setSendError("Save your details in step 1 first, then try again.");
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/welocate/applications/${applicationId}/submit`,
        { method: "POST" }
      );
      if (!response.ok) {
        throw new Error(`Submitting failed (${response.status})`);
      }
      setIsSubmitted(true);
    } catch (error) {
      console.error("Could not submit application", error);
      setSendError(
        "We could not send your application. Check your connection and try again."
      );
    } finally {
      setSending(false);
    }
  };

  const property = useMemo(() => {
    if (!listing) return application.property;
    const name = [listing.street, listing.house_number, listing.floor]
      .filter(Boolean)
      .join(" ");
    const metaLine = [
      [listing.postcode, listing.city].filter(Boolean).join(" "),
      listing.area != null ? `${listing.area} m²` : null,
      listing.bedrooms != null
        ? `${listing.bedrooms} bedroom${listing.bedrooms === 1 ? "" : "s"}`
        : null,
      listing.is_furnished == null
        ? null
        : listing.is_furnished
        ? "Furnished"
        : "Unfurnished",
    ]
      .filter(Boolean)
      .join(" · ");
    return {
      ...application.property,
      name: name || application.property.name,
      city: listing.city || application.property.city,
      metaLine: metaLine || application.property.metaLine,
      askingRent:
        listing.monthly_rent != null
          ? formatEuro(listing.monthly_rent)
          : application.property.askingRent,
      freeFrom: listing.available_date
        ? formatDate(listing.available_date)
        : application.property.freeFrom,
    };
  }, [listing, application]);

  // Only warn when the backend explicitly says the flag is false; an absent
  // listing or field means we simply do not know.
  const studentConflict =
    listing?.students_allowed === false && profile === "student";
  const petsConflict =
    listing?.pets_allowed === false && household.pets === "yes";
  const sharingConflict =
    listing?.sharing_allowed === false && household.withPartner === "yes";

  const offerRent = Number(offer.rent);
  const offerIncome = combinedIncome(profile, employment, household);
  const incomeConflict =
    offerRent > 0 &&
    offerIncome > 0 &&
    offerIncome < offerRent * CRITERIA_RULES.incomeMultiplier;

  // "As soon as it is free" and the fixed date options all fall inside the
  // landlord's window, so only a custom date needs checking.
  const startMet =
    offer.startChoice === "custom"
      ? offer.customStart >= CRITERIA_RULES.startWindow.from &&
        offer.customStart <= CRITERIA_RULES.startWindow.to
      : true;

  // A student's income is the guarantor's and a self-employed applicant's
  // comes from the tax declarations; both only show from the documents in
  // step 2, so there is nothing to check live.
  const criteriaChecks =
    profile !== "employed"
      ? []
      : [
          {
            label: `Income ${CRITERIA_RULES.incomeMultiplier}× the rent`,
            met:
              offerRent > 0 &&
              offerIncome >= offerRent * CRITERIA_RULES.incomeMultiplier,
          },
        ];

  const propertyImage =
    listing?.image_url || application.imageUrl || propertyPhoto;

  // A required document is handled by an accepted upload or by being marked
  // as not applicable.
  const handledRequired = requiredDocs.filter(
    (doc) => uploads[doc.id]?.status === "accepted" || waivers[doc.id]
  ).length;
  const missingRequired = requiredDocs.length - handledRequired;
  const selectedDoc =
    allDocs.find((doc) => doc.id === selectedDocId) ?? allDocs[0];
  const selectedUpload = uploads[selectedDocId];
  const selectedWaiver = uploads[selectedDocId] ? null : waivers[selectedDocId];
  const nextRequiredDoc = requiredDocs.find(
    (doc) =>
      uploads[doc.id]?.status !== "accepted" &&
      !waivers[doc.id] &&
      doc.id !== selectedDocId
  );

  if (listingStatus !== "found") {
    return (
      <div className="va-page">
        <header className="va-topbar">
          <img className="va-logo" src={wlLogoWhite} alt="Welocate" />
          <h1 className="va-topbar-title">Application for renting property</h1>
          <div className="va-topbar-right">
            <span className="va-secure">
              <LockOutlinedIcon />
              Secure form
            </span>
          </div>
        </header>
        <main className="va-main">
          {listingStatus === "not-found" && (
            <section className="va-card va-not-found">
              <h2 className="va-not-found-title">Listing not found</h2>
              <p className="va-not-found-text">
                We could not find the home this link refers to. It may have
                been taken off the market, or the link is incomplete. Please
                check the link you received from Welocate.
              </p>
            </section>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="va-page">
      <header className="va-topbar">
        <img className="va-logo" src={wlLogoWhite} alt="Welocate" />
        <h1 className="va-topbar-title">
          Application for renting property · {property.name}, {property.city}
        </h1>
        <div className="va-topbar-right">
          <span className="va-secure">
            <LockOutlinedIcon />
            Secure form
          </span>
        </div>
      </header>

      <main className="va-main">
        {isSubmitted && (
          <div className="va-submitted-banner">
            <CheckIcon className="va-submitted-banner-icon" />
            <p>
              Your application has been sent to the landlord and can no longer
              be changed. You can still look through everything you filled in.
            </p>
          </div>
        )}
        <div className="va-steps">
          <button
            type="button"
            className={`va-step${step === 1 ? " va-step--active" : ""}`}
            onClick={() => setStep(1)}
          >
            <span className="va-step-number">1</span>
            <span className="va-step-text">
              <span className="va-step-title">Your details &amp; offer</span>
              <span className="va-step-sub">
                {detailsToFix} to fix
              </span>
            </span>
          </button>
          <button
            type="button"
            className={`va-step${step === 2 ? " va-step--active" : ""}`}
            onClick={submitDetails}
            disabled={submitting || (!isSubmitted && !formComplete)}
          >
            <span className="va-step-number">2</span>
            <span className="va-step-text">
              <span className="va-step-title">Your documents</span>
              <span className="va-step-sub">{missingRequired} missing</span>
            </span>
          </button>
        </div>

        <div className="va-columns">
          <div className="va-content">
            {/* A disabled fieldset greys out every control once the
                application has been sent. */}
            <fieldset className="va-fieldset" disabled={isSubmitted}>
            {step === 1 ? (
              <>
                <div className="va-intro">
                  <LockOutlinedIcon className="va-intro-icon" />
                  <p>
                    Applying takes two short steps: your details and your
                    documents. Everything you share is stored securely and
                    handled according to privacy regulations, and we delete it
                    if your application is not accepted.
                  </p>
                </div>

                <section className="va-card">
                  <div className="va-card-heading">
                    <span className="va-chip">Step 1 of 2</span>
                    <div>
                      <h2 className="va-card-title">
                        Which of these describes you?
                      </h2>
                      <p className="va-card-sub">
                        This decides which documents we ask for later. Pick
                        carefully, you can change it before sending.
                      </p>
                    </div>
                  </div>
                  <div className="va-profiles">
                    {PROFILE_OPTIONS.map(
                      ({ id, label, description, Icon, disabled }) => (
                        <button
                          key={id}
                          type="button"
                          className={`va-profile${
                            profile === id ? " va-profile--selected" : ""
                          }${disabled ? " va-profile--disabled" : ""}`}
                          onClick={() => setProfile(id)}
                          disabled={disabled}
                          aria-disabled={disabled || undefined}
                        >
                          <Icon className="va-profile-icon" />
                          <span className="va-profile-label">{label}</span>
                          <span className="va-profile-desc">{description}</span>
                          {disabled && (
                            <span className="va-profile-soon">Coming soon</span>
                          )}
                        </button>
                      )
                    )}
                  </div>
                  {studentConflict && (
                    <ConflictWarning>
                      This landlord does not accept students for this home.
                      You can still apply, but expect your application to be
                      declined.
                    </ConflictWarning>
                  )}
                </section>

                <section className="va-card">
                  <h2 className="va-card-title">Your details</h2>

                  <div className="va-grid va-grid--3">
                    <Field label="First name">
                      <input
                        id="field-first-name"
                        className="va-input"
                        value={details.firstName}
                        onChange={setField("firstName")}
                      />
                    </Field>
                    <Field label="Last name">
                      <input
                        id="field-last-name"
                        className="va-input"
                        value={details.lastName}
                        onChange={setField("lastName")}
                      />
                    </Field>
                    <Field label="Nationality">
                      <select
                        id="field-nationality"
                        className="va-input"
                        value={details.nationality}
                        onChange={setField("nationality")}
                      >
                        <option value="" disabled>
                          Select nationality
                        </option>
                        {NATIONALITY_OPTIONS.map((nationality) => (
                          <option key={nationality} value={nationality}>
                            {nationality}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="va-grid va-grid--2">
                    <Field label="Email" error={emailError}>
                      <input
                        id="field-email"
                        className={`va-input${
                          emailError ? " va-input--error" : ""
                        }`}
                        type="email"
                        value={details.email}
                        onChange={setField("email")}
                      />
                    </Field>
                    <Field label="Mobile" error={mobileError}>
                      <input
                        id="field-mobile"
                        className={`va-input${
                          mobileError ? " va-input--error" : ""
                        }`}
                        type="tel"
                        value={details.mobile}
                        onChange={setField("mobile")}
                      />
                    </Field>
                  </div>

                  <div className="va-grid va-grid--address">
                    <Field label="Current address (street)">
                      <input
                        id="field-street"
                        className="va-input"
                        maxLength={MAX_STREET_LENGTH}
                        value={details.street}
                        onChange={setField("street")}
                      />
                    </Field>
                    <Field label="House no.">
                      <input
                        id="field-house-no"
                        className="va-input"
                        type="number"
                        step="1"
                        min="1"
                        max={MAX_HOUSE_NUMBER}
                        value={details.houseNo}
                        onKeyDown={blockNonIntegerKeys}
                        onChange={(event) =>
                          setDetails((prev) => ({
                            ...prev,
                            houseNo: toClampedInt(
                              event.target.value,
                              MAX_HOUSE_NUMBER
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Addition" optional>
                      <input
                        className="va-input"
                        value={details.addition}
                        onChange={setField("addition")}
                      />
                    </Field>
                  </div>

                  <div className="va-grid va-grid--3">
                    <Field label="Postcode">
                      <input
                        id="field-postcode"
                        className="va-input"
                        maxLength={MAX_POSTCODE_LENGTH}
                        value={details.postcode}
                        onChange={setField("postcode")}
                      />
                    </Field>
                    <Field label="City">
                      <input
                        id="field-city"
                        className="va-input"
                        value={details.city}
                        onChange={setField("city")}
                      />
                    </Field>
                    <Field label="Country">
                      <select
                        id="field-country"
                        className="va-input"
                        value={details.country}
                        onChange={setField("country")}
                      >
                        <option value="" disabled>
                          Select country
                        </option>
                        {COUNTRY_OPTIONS.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  {profile === "employed" && (
                    <div className="va-subsection">
                      <h3 className="va-subsection-title">Your employment</h3>
                      <div className="va-grid va-grid--2">
                        <Field label="Employer">
                          <input
                            id="field-employer"
                            className="va-input"
                            maxLength={MAX_EMPLOYER_LENGTH}
                            value={employment.employer}
                            onChange={setEmploymentField("employer")}
                          />
                        </Field>
                        <Field label="Monthly gross income (EUR)">
                          <div className="va-input va-input--euro">
                            <span>€</span>
                            <input
                              id="field-income"
                              type="number"
                              step="1"
                              min="0"
                              max={MAX_MONTHLY_INCOME}
                              value={employment.income}
                              onKeyDown={blockNonIntegerKeys}
                              onChange={(event) =>
                                setEmployment((prev) => ({
                                  ...prev,
                                  income: toClampedInt(
                                    event.target.value,
                                    MAX_MONTHLY_INCOME
                                  ),
                                }))
                              }
                            />
                          </div>
                        </Field>
                      </div>
                      <div className="va-choice-group">
                        <label className="va-label">
                          Employment contract length
                          <span className="va-required">*</span>
                        </label>
                        <div className="va-pills">
                          {EMPLOYMENT_LENGTH_OPTIONS.map(({ id, label }) => (
                            <button
                              key={id}
                              type="button"
                              className={`va-pill${
                                employment.contractLength === id
                                  ? " va-pill--selected"
                                  : ""
                              }`}
                              onClick={() =>
                                setEmployment((prev) => ({
                                  ...prev,
                                  contractLength: id,
                                }))
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                <section className="va-card">
                  <h2 className="va-card-title">Household</h2>
                  <p className="va-card-sub">
                    This home is registered for 2 occupants and the building
                    does not allow pets.
                  </p>

                  <div className="va-grid va-grid--3">
                    <Field
                      label={
                        profile === "student"
                          ? "Renting with a 2nd tenant?"
                          : "Renting with a partner or friend?"
                      }
                    >
                      <select
                        className="va-input"
                        value={household.withPartner}
                        onChange={setHouseholdField("withPartner")}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                    <Field label="Number in household" error={countError}>
                      <input
                        id="field-household-count"
                        className={`va-input${
                          countError ? " va-input--error" : ""
                        }`}
                        type="number"
                        min="1"
                        max={MAX_HOUSEHOLD_COUNT}
                        value={household.count}
                        onChange={setHouseholdField("count")}
                      />
                    </Field>
                    <Field label="Pets?">
                      <select
                        className="va-input"
                        value={household.pets}
                        onChange={setHouseholdField("pets")}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                  </div>

                  {/* {sharingConflict && (
                    <ConflictWarning>
                      Sharing is not allowed in this home. The landlord does
                      not accept applications from people renting together.
                    </ConflictWarning>
                  )} */}
                  {petsConflict && (
                    <ConflictWarning>
                      Pets are not allowed in this home. Applying with a pet
                      will most likely get your application declined.
                    </ConflictWarning>
                  )}

                  {household.pets === "yes" && (
                    <div className="va-grid va-grid--2">
                      <Field label="Which pet?">
                        <input
                          id="field-pet-details"
                          className="va-input"
                          value={household.petDetails}
                          onChange={setHouseholdField("petDetails")}
                        />
                      </Field>
                    </div>
                  )}

                  {household.withPartner === "yes" && (
                    <div className="va-subsection">
                      <h3 className="va-subsection-title">
                        {profile === "student"
                          ? "The 2nd tenant"
                          : "Your partner or friend"}
                      </h3>
                      <div className="va-grid va-grid--2">
                        <Field label="First name">
                          <input
                            id="field-partner-first-name"
                            className="va-input"
                            value={household.partnerFirstName}
                            onChange={setHouseholdField("partnerFirstName")}
                          />
                        </Field>
                        <Field label="Last name">
                          <input
                            id="field-partner-last-name"
                            className="va-input"
                            value={household.partnerLastName}
                            onChange={setHouseholdField("partnerLastName")}
                          />
                        </Field>
                      </div>
                      <div className="va-grid va-grid--2">
                        <Field label="Email" error={partnerEmailError}>
                          <input
                            id="field-partner-email"
                            className={`va-input${
                              partnerEmailError ? " va-input--error" : ""
                            }`}
                            type="email"
                            value={household.partnerEmail}
                            onChange={setHouseholdField("partnerEmail")}
                          />
                        </Field>
                        <Field label="Mobile" error={partnerMobileError}>
                          <input
                            id="field-partner-mobile"
                            className={`va-input${
                              partnerMobileError ? " va-input--error" : ""
                            }`}
                            type="tel"
                            value={household.partnerMobile}
                            onChange={setHouseholdField("partnerMobile")}
                          />
                        </Field>
                      </div>
                      <div
                        className={`va-grid ${
                          profile === "employed" ? "va-grid--4" : "va-grid--2"
                        }`}
                      >
                        <Field label="Nationality">
                          <select
                            id="field-partner-nationality"
                            className="va-input"
                            value={household.partnerNationality}
                            onChange={setHouseholdField("partnerNationality")}
                          >
                            <option value="" disabled>
                              Select nationality
                            </option>
                            {NATIONALITY_OPTIONS.map((nationality) => (
                              <option key={nationality} value={nationality}>
                                {nationality}
                              </option>
                            ))}
                          </select>
                        </Field>
                        {profile === "employed" && (
                          <>
                          <Field label="Employer">
                            <input
                              id="field-partner-employer"
                              className="va-input"
                              maxLength={MAX_EMPLOYER_LENGTH}
                              value={household.partnerEmployer}
                              onChange={setHouseholdField("partnerEmployer")}
                            />
                          </Field>
                          <Field label="Contract length">
                            <select
                              id="field-partner-contract-length"
                              className="va-input"
                              value={household.partnerContractLength}
                              onChange={setHouseholdField("partnerContractLength")}
                            >
                              <option value="" disabled>
                                Select length
                              </option>
                              {EMPLOYMENT_LENGTH_OPTIONS.map(({ id, label }) => (
                                <option key={id} value={id}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Monthly gross income (EUR)">
                            <div className="va-input va-input--euro">
                              <span>€</span>
                              <input
                                id="field-partner-income"
                                type="number"
                                step="1"
                                min="0"
                                max={MAX_MONTHLY_INCOME}
                                value={household.partnerIncome}
                                onKeyDown={blockNonIntegerKeys}
                                onChange={(event) =>
                                  setHousehold((prev) => ({
                                    ...prev,
                                    partnerIncome: toClampedInt(
                                      event.target.value,
                                      MAX_MONTHLY_INCOME
                                    ),
                                  }))
                                }
                              />
                            </div>
                            </Field>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="va-grid">
                    <Field
                      label="Anything else the landlord should know?"
                      optional
                      helper="Short is fine. A sentence about who you are and why this home."
                    >
                      <textarea
                        className="va-input va-textarea"
                        rows={4}
                        value={household.note}
                        onChange={setHouseholdField("note")}
                      />
                    </Field>
                  </div>
                </section>

                <section className="va-card">
                  <h2 className="va-card-title">Your offer</h2>
                  <p className="va-card-sub">
                    Asking rent is {property.askingRent} per month.
                  </p>

                  <div className="va-grid va-grid--2">
                    <Field
                      label="Your monthly rental offer (EUR)"
                      helper={
                        profile === "student"
                          ? "Rule of thumb: this landlord wants your guarantor's income to be 3× the rent you offer."
                          : profile === "self-employed"
                          ? "Rule of thumb: this landlord wants your income from your tax declarations to be 3× the rent you offer."
                          : "Rule of thumb: this landlord wants a household income of 3× the rent you offer."
                      }
                    >
                      <div className="va-input va-input--euro">
                        <span>€</span>
                        <input
                          id="field-offer-rent"
                          type="number"
                          step="1"
                          min="0"
                          max={MAX_RENT_OFFER}
                          value={offer.rent}
                          onKeyDown={blockNonIntegerKeys}
                          onChange={(event) =>
                            setOffer((prev) => ({
                              ...prev,
                              rent: toClampedInt(
                                event.target.value,
                                MAX_RENT_OFFER
                              ),
                            }))
                          }
                        />
                      </div>
                    </Field>
                  </div>

                  {incomeConflict && (
                    <ConflictWarning>
                      Your household income is below 3 times the rent you
                      offer. For an offer of {formatEuro(offerRent)} this
                      landlord expects an income of at least{" "}
                      {formatEuro(offerRent * 3)} per month.
                    </ConflictWarning>
                  )}

                  <div className="va-choice-group">
                    <label className="va-label">
                      Desired contract length
                      <span className="va-required">*</span>
                    </label>
                    <div className="va-pills">
                      {CONTRACT_OPTIONS.map(({ months, note, label }) => (
                        <button
                          key={months}
                          type="button"
                          className={`va-pill${
                            offer.contractMonths === months
                              ? " va-pill--selected"
                              : ""
                          }`}
                          onClick={() =>
                            setOffer((prev) => ({
                              ...prev,
                              contractMonths: months,
                            }))
                          }
                        >
                          {label ?? `${months} months`}
                          {note && <span className="va-pill-note">{note}</span>}
                        </button>
                      ))}
                    </div>
                    <p className="va-helper">
                      This landlord asks for 12 months or longer.
                    </p>
                  </div>

                  <div className="va-choice-group">
                    <label className="va-label">
                      Desired start date
                      <span className="va-required">*</span>
                    </label>
                    <div className="va-pills">
                      {START_OPTIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          className={`va-pill${
                            offer.startChoice === id ? " va-pill--selected" : ""
                          }`}
                          onClick={() =>
                            setOffer((prev) => ({ ...prev, startChoice: id }))
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <input
                      id="field-custom-start"
                      className="va-input va-input--date"
                      type="date"
                      value={offer.customStart}
                      onChange={(event) =>
                        setOffer((prev) => ({
                          ...prev,
                          startChoice: "custom",
                          customStart: event.target.value,
                        }))
                      }
                    />
                    <p className="va-helper">
                      Free from {property.freeFrom}
                    </p>
                  </div>
                </section>

                <section className="va-card">
                  <label className="va-confirm">
                    <input
                      id="field-confirm"
                      type="checkbox"
                      checked={confirmed}
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <span>
                      I confirm the information is accurate and give Welocate
                      permission to verify my documents and share this
                      application with the landlord of {property.name}.
                      Documents are kept confidential and deleted if the
                      application does not proceed.
                    </span>
                  </label>
                </section>
              </>
            ) : (
              <>
                <section className="va-card">
                  <div className="va-card-heading va-card-heading--docs">
                    <span className="va-chip">Step 2 of 2</span>
                    <div>
                      <h2 className="va-card-title">Your documents</h2>
                      <p className="va-card-sub">
                        Here you can upload the documents for your application.
                      </p>
                    </div>
                    <span className="va-docs-count">
                      {handledRequired}/{requiredDocs.length}
                    </span>
                  </div>
                </section>

                <section className="va-card">
                  <div className="va-doc-head">
                    <div>
                      <h2 className="va-card-title">{selectedDoc.label}</h2>
                      <p className="va-card-sub">
                        {selectedDoc.optional
                          ? "Optional for your situation"
                          : "Required for your application"}
                        {selectedDoc.hint ? ` · ${selectedDoc.hint}` : ""}
                      </p>
                    </div>
                    {selectedUpload?.status === "processing" ? (
                      <span className="va-doc-status va-doc-status--reading">
                        <span className="va-spinner" />
                        Reading your file
                      </span>
                    ) : selectedUpload?.status === "accepted" ? (
                      <span className="va-doc-status va-doc-status--accepted">
                        <CheckIcon />
                        Accepted
                      </span>
                    ) : selectedUpload ? (
                      <span className="va-doc-status va-doc-status--attention">
                        <PriorityHighIcon />
                        Needs a new file
                      </span>
                    ) : selectedWaiver ? (
                      <span className="va-doc-status va-doc-status--waived">
                        <CheckIcon />
                        Not applicable
                      </span>
                    ) : (
                      <span className="va-doc-status">
                        Waiting for your file
                      </span>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={
                      selectedDoc.pdfOnly
                        ? "application/pdf"
                        : "application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                    }
                    hidden
                    onChange={(event) => {
                      handleFile(selectedDocId, event.target.files[0]);
                      event.target.value = "";
                    }}
                  />

                  {!selectedUpload && !selectedWaiver && !waiverOpen && (
                    <>
                      <div
                        className="va-dropzone"
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          handleFile(
                            selectedDocId,
                            event.dataTransfer.files[0]
                          );
                        }}
                      >
                        <ArrowUpwardIcon className="va-dropzone-icon" />
                        <span className="va-dropzone-title">
                          Drop {selectedDoc.label.toLowerCase()} here
                        </span>
                        <span className="va-dropzone-sub">
                          or{" "}
                          <button
                            type="button"
                            className="va-linklike"
                            onClick={() => fileInputRef.current.click()}
                          >
                            browse your files
                          </button>{" "}
                          ·{" "}
                          {selectedDoc.pdfOnly
                            ? "PDF up to 10 MB"
                            : "PDF, DOCX or photo up to 10 MB"}
                        </span>
                      </div>
                      {!selectedDoc.optional && (
                        <p className="va-waiver-prompt">
                          Do you think this document doesn't apply to you?{" "}
                          <button
                            type="button"
                            className="va-linklike"
                            onClick={() => setWaiverOpen(true)}
                          >
                            Click here
                          </button>
                        </p>
                      )}
                    </>
                  )}

                  {!selectedUpload && !selectedWaiver && waiverOpen && (
                    <div className="va-waiver-form">
                      <label className="va-label">
                        Why doesn't this document apply to you?
                        <span className="va-required">*</span>
                      </label>
                      <textarea
                        className="va-input va-textarea"
                        rows={3}
                        placeholder={`Tell us in a sentence or two why you cannot provide a ${selectedDoc.label.toLowerCase()}.`}
                        value={waiverReason}
                        onChange={(event) =>
                          setWaiverReason(event.target.value)
                        }
                      />
                      {waiverError && (
                        <p className="va-waiver-error">{waiverError}</p>
                      )}
                      <div className="va-waiver-actions">
                        <button
                          type="button"
                          className="va-waiver-submit"
                          onClick={submitWaiver}
                          disabled={waiverSaving}
                        >
                          {waiverSaving ? (
                            <>
                              <span className="va-spinner" />
                              Saving
                            </>
                          ) : (
                            "Submit reason"
                          )}
                        </button>
                        <button
                          type="button"
                          className="va-file-button"
                          onClick={() => {
                            setWaiverOpen(false);
                            setWaiverReason("");
                            setWaiverError(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {!selectedUpload && selectedWaiver && (
                    <div className="va-waived">
                      <span className="va-waived-label">
                        Marked as not applicable
                      </span>
                      <p className="va-waived-reason">
                        {selectedWaiver.reason}
                      </p>
                      <button
                        type="button"
                        className="va-file-button"
                        onClick={() => removeWaiver(selectedDocId)}
                      >
                        It does apply, upload a file instead
                      </button>
                    </div>
                  )}

                  {selectedUpload?.status === "processing" && (
                    <div className="va-dropzone va-dropzone--reading">
                      <span className="va-spinner va-spinner--large" />
                      <span className="va-dropzone-title">
                        Reading {selectedUpload.name}
                      </span>
                      <span className="va-dropzone-sub">
                        We check it against the landlord's requirements. This
                        usually takes a few seconds.
                      </span>
                    </div>
                  )}

                  {selectedUpload && selectedUpload.status !== "processing" && (
                    <>
                      <div className="va-file-row">
                        <span className="va-file-icon">
                          <AttachFileIcon />
                        </span>
                        <span className="va-file-text">
                          <span className="va-file-name">
                            {selectedUpload.name}
                          </span>
                          <span className="va-file-meta">
                            {selectedUpload.size != null
                              ? `${formatFileSize(
                                  selectedUpload.size
                                )} · stored encrypted`
                              : "Stored encrypted"}
                          </span>
                        </span>
                        <span className="va-file-actions">
                          <button
                            type="button"
                            className="va-file-button"
                            onClick={() => fileInputRef.current.click()}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="va-file-button"
                            onClick={() => removeUpload(selectedDocId)}
                          >
                            Remove
                          </button>
                        </span>
                      </div>

                      <div
                        className={`va-result${
                          selectedUpload.status === "accepted"
                            ? " va-result--ok"
                            : " va-result--warn"
                        }`}
                      >
                        <span className="va-result-icon">
                          {selectedUpload.status === "accepted" ? (
                            <CheckIcon />
                          ) : (
                            <PriorityHighIcon />
                          )}
                        </span>
                        <span className="va-result-text">
                          <span className="va-result-headline">
                            {selectedUpload.headline}
                          </span>
                          {selectedUpload.detail && (
                            <span className="va-result-detail">
                              {selectedUpload.detail}
                            </span>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </section>

                {nextRequiredDoc && (
                  <button
                    type="button"
                    className="va-card va-next-card"
                    onClick={() => selectDoc(nextRequiredDoc.id)}
                  >
                    <span className="va-next-text">
                      <span className="va-next-label">
                        Next required document
                      </span>
                      <span className="va-next-doc">
                        {nextRequiredDoc.label}
                      </span>
                    </span>
                    <ChevronRightIcon />
                  </button>
                )}

                <section className="va-card va-send-card">
                  <div className="va-send-text">
                    <h2 className="va-card-title">Send your application</h2>
                    <p className="va-card-sub">
                      {isSubmitted
                        ? "Your application has been sent to the landlord."
                        : handledRequired < requiredDocs.length
                        ? `${requiredDocs.length - handledRequired} required ${
                            requiredDocs.length - handledRequired === 1
                              ? "document is"
                              : "documents are"
                          } still missing. You can already send, but a complete application is reviewed faster.`
                        : "All required documents are handled. Send your application to the landlord."}
                    </p>
                    {sendError && <p className="va-send-error">{sendError}</p>}
                  </div>
                  {isSubmitted ? (
                    <p className="va-footer-success">
                      <CheckIcon />
                      Application sent
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="va-continue"
                      onClick={sendApplication}
                      disabled={sending || !confirmed}
                    >
                      {sending ? (
                        <>
                          <span className="va-spinner" />
                          Sending your application
                        </>
                      ) : (
                        "Send application"
                      )}
                    </button>
                  )}
                </section>
              </>
            )}
            </fieldset>
          </div>

          <aside className="va-sidebar">
            {step === 2 && (
              <section className="va-card va-card--aside va-doc-panel">
                <h3 className="va-doc-panel-title">Your documents</h3>
                <p className="va-doc-panel-sub">
                  <strong>{handledRequired}</strong> of{" "}
                  <strong>{requiredDocs.length}</strong> required documents
                  handled
                </p>
                <div className="va-doc-progress">
                  <div
                    className="va-doc-progress-fill"
                    style={{
                      width: `${
                        (handledRequired / requiredDocs.length) * 100
                      }%`,
                    }}
                  />
                </div>
                {documentSections.map((section) => {
                  const isOpen = section.title === openSectionTitle;
                  const sectionRequired = section.docs.filter(
                    (doc) => !doc.optional
                  );
                  const sectionHandled = sectionRequired.filter(
                    (doc) =>
                      uploads[doc.id]?.status === "accepted" || waivers[doc.id]
                  ).length;
                  return (
                    <div
                      key={section.title}
                      className={`va-doc-section${
                        isOpen ? " va-doc-section--open" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="va-doc-section-head"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setOpenSectionTitle(isOpen ? null : section.title)
                        }
                      >
                        <h4 className="va-doc-section-title">{section.title}</h4>
                        <span
                          className={`va-doc-section-count${
                            sectionRequired.length > 0 &&
                            sectionHandled === sectionRequired.length
                              ? " va-doc-section-count--done"
                              : ""
                          }`}
                        >
                          {sectionHandled}/{sectionRequired.length}
                        </span>
                        <ChevronRightIcon className="va-doc-section-chevron" />
                      </button>
                      {isOpen && section.docs.map((doc) => {
                        const upload = uploads[doc.id];
                        const waiver = upload ? null : waivers[doc.id];
                        return (
                          <button
                            key={doc.id}
                            type="button"
                            className={`va-doc-item${
                              doc.id === selectedDocId
                                ? " va-doc-item--active"
                                : ""
                            }`}
                            onClick={() => selectDoc(doc.id)}
                          >
                            <span
                              className={`va-doc-circle${
                                upload?.status === "accepted"
                                  ? " va-doc-circle--accepted"
                                  : upload?.status === "rejected"
                                  ? " va-doc-circle--attention"
                                  : waiver
                                  ? " va-doc-circle--waived"
                                  : ""
                              }`}
                            >
                              {upload?.status === "accepted" && <CheckIcon />}
                              {upload?.status === "rejected" && (
                                <PriorityHighIcon />
                              )}
                              {waiver && <CheckIcon />}
                            </span>
                            <span className="va-doc-item-text">
                              <span className="va-doc-item-name">
                                {doc.label}
                              </span>
                              <span className="va-doc-item-status">
                                {upload?.status === "processing"
                                  ? "Reading your file"
                                  : upload?.status === "accepted"
                                  ? "Accepted"
                                  : upload
                                  ? "Needs a new file"
                                  : waiver
                                  ? "Not applicable"
                                  : "Not uploaded"}
                              </span>
                            </span>
                            {doc.optional && (
                              <span className="va-doc-tag">Optional</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </section>
            )}

            {step === 1 && (
              <>
            <section className="va-card va-card--property">
              <img
                className="va-property-photo"
                src={propertyImage}
                alt={property.name}
              />
              <div className="va-property-body">
                <h3 className="va-property-name">{property.name}</h3>
                <p className="va-property-meta">{property.metaLine}</p>
                <dl className="va-property-facts">
                  <div className="va-fact">
                    <dt>Asking rent</dt>
                    <dd>
                      {property.askingRent}
                      <span className="va-permonth">/mo</span>
                    </dd>
                  </div>
                  <div className="va-fact">
                    <dt>Free from</dt>
                    <dd>{property.freeFrom}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="va-card va-card--aside">
              <h3 className="va-aside-title">Landlord's criteria</h3>
              {profile === "student" && (
                <p className="va-aside-body">
                  As a student, your guarantor's income counts instead of your
                  own. This landlord expects it to be{" "}
                  {CRITERIA_RULES.incomeMultiplier}× the rent you offer; we
                  check it from the guarantor documents in step 2.
                </p>
              )}
              {profile === "self-employed" && (
                <p className="va-aside-body">
                  As a self-employed applicant, your income is read from your
                  tax declarations. This landlord expects it to be{" "}
                  {CRITERIA_RULES.incomeMultiplier}× the rent you offer; we
                  check it from the business documents in step 2.
                </p>
              )}
              <ul className="va-criteria">
                {criteriaChecks.map(({ label, met }) => (
                  <li
                    key={label}
                    className={`va-criterion ${
                      met ? "va-criterion--met" : "va-criterion--unmet"
                    }`}
                  >
                    {met ? (
                      <CheckIcon className="va-criterion-icon" />
                    ) : (
                      <CloseIcon className="va-criterion-icon" />
                    )}
                    {label}
                  </li>
                ))}
              </ul>
            </section>

            <section className="va-card va-card--aside">
              <h3 className="va-aside-title">Why we ask</h3>
              <p className="va-aside-body">
                Rules like "income 3× the rent" come from the landlord, not
                from us. We show them upfront so you know where you stand
                before you spend time on a viewing.
              </p>
            </section>
              </>
            )}
          </aside>
        </div>
      </main>

      <footer className="va-footer">
        <p className="va-footer-status">
          Step {step} of 2
          {step === 2 && (
            <>
              {" · "}
              <strong>
                {handledRequired}/{requiredDocs.length}
              </strong>{" "}
              documents
            </>
          )}
          {isSubmitted && " · application sent"}
        </p>
        {step === 1 ? (
          <div className="va-footer-actions">
            {submitError && <p className="va-footer-error">{submitError}</p>}
            {!submitError && !isSubmitted && incompleteHint && (
              <button
                type="button"
                className="va-footer-hint"
                onClick={() => focusField(incompleteHint.target)}
              >
                {incompleteHint.message}
              </button>
            )}
            <button
              type="button"
              className="va-continue"
              onClick={submitDetails}
              disabled={submitting || (!isSubmitted && !formComplete)}
            >
              {submitting ? (
                <>
                  <span className="va-spinner" />
                  Saving your details
                </>
              ) : (
                <>
                  Continue to documents
                  <ChevronRightIcon />
                </>
              )}
            </button>
          </div>
        ) : (
          <>
            {isSubmitted ? (
              <p className="va-footer-success va-footer-middle">
                <CheckIcon />
                Application sent. Welocate will contact you about the application.
              </p>
            ) : sendError ? (
              <p className="va-footer-error va-footer-middle">{sendError}</p>
            ) : (
              <p className="va-footer-notice va-footer-middle">
                Not sent yet. The landlord only receives your application
                once you press Send.
              </p>
            )}
            <div className="va-footer-actions">
              <button
                type="button"
                className="va-back"
                onClick={() => setStep(1)}
              >
                <ChevronLeftIcon />
                Back
              </button>
            </div>
          </>
        )}
      </footer>
    </div>
  );
};

export default ViewingApplication;
